import { defineStore, storeToRefs } from "pinia";
import * as Sentry from "@sentry/vue";
import { computed, ref, watch } from "vue";
import type { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import { getOwners } from "@/api/services/Owners";

import { useProject } from "@/stores/project/project.store";
import { useLibrary } from "@/stores/project/library.store";
import { useEnum } from "@/stores/project/project.enum";
import { useGetTenantCollectionQuery, useGetAllTenantCollectionItemsQuery, useGetCategoryTranslationsQuery } from "@/graphql";
import type { GetTenantCollectionQuery } from "@/graphql";
import { useCollectionsProvider } from "@/dataProviders/Collections/providers/CollectionsProvider";
import useHasuraStore from "@/stores/hasura/hasura.store";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";

import type { ProductType, SpecificationForm, Tag, UnitOption } from "@/stores/project/types/enum";
import withApolloClient from "@/graphql/services/withApolloClient";

export type Owner = {
  id: string;
  name: string;
  city: string;
  country: string | null;
  email: string;
  phoneNumber: string;
  vatNumber: string;
  streetName: string;
  houseNumber: string;
  postalCode: string;
};

export type CategoryFilter = {
  label: string;
  value: string;
};

type ProductTypeWithCode = {
  Enum: "INDUSTRY" | "PRODUCT" | "GENERIC" | "CUSTOM";
  DisplayName: string;
  code?: string;
  value?: boolean;
};
type SpecWithCode = { Enum: "EN15804A1" | "EN15804A2"; DisplayName: string; code: "0" | "1" };
type EnumDisplay<T extends string | number = string> = { Enum: T; DisplayName: string };

type LibraryFilters = {
  material: {
    collections: GetTenantCollectionQuery["tenantCollection"];
    specifications: SpecWithCode[] | null;
    types: ProductTypeWithCode[] | null;
    owners: Owner[] | null;
    datasources: { id: string; name: string; sourceFormat: string; url: string }[] | null;
    categories: CategoryFilter[] | null;
    tags: string[] | null;
    organisations: { id: any; DisplayName: string }[] | null;
    indicators: EnumDisplay<number>[] | null;
    minValidTo: string | null;
  };
  construction: {
    collections: GetTenantCollectionQuery["tenantCollection"];
    units: UnitOption[] | null;
    tags: string[] | null;
    owners: Owner[] | null;
    organisations: { id: string; DisplayName: string }[] | null;
  };
};

const A = <T>(v: T[] | Record<string | number, T> | null | undefined): T[] => (Array.isArray(v) ? v : v ? Object.values(v) : []);

const PRODUCT_TYPE_CODE: Record<"INDUSTRY" | "PRODUCT" | "GENERIC", "0" | "1" | "2"> = {
  INDUSTRY: "0",
  PRODUCT: "1",
  GENERIC: "2",
};
const SPEC_CODE: Record<"EN15804A1" | "EN15804A2", "0" | "1"> = {
  EN15804A1: "0",
  EN15804A2: "1",
};

export const useFilters = defineStore("filters", () => {
  const projectStore = useProject();
  const libraryStore = useLibrary();
  const { isConstruction } = storeToRefs(libraryStore);

  const enumStore = useEnum();
  const { specificationForms, productTypes, tagsTypesForms, datasources, tenants } = storeToRefs(enumStore);

  // Collections
  const { status: hasuraStatus } = storeToRefs(useHasuraStore());
  const authState = useAuthStateStore();
  // A malformed (non-UUID) tenantId must not reach these queries — see AuthState.isTenantIdValid.
  const collectionsEnabled = computed(() => hasuraStatus.value === "authenticated" && authState.isTenantIdValid);
  const collectionsQuery = withApolloClient(() =>
    useGetTenantCollectionQuery(computed(() => ({ enabled: collectionsEnabled.value })))
  );
  const itemsQuery = withApolloClient(() =>
    useGetAllTenantCollectionItemsQuery(computed(() => ({ enabled: collectionsEnabled.value })))
  );
  // Static reference data behind the material "Category" group, fetched once for every FilterBox that
  // lists the material groups (Library toolbar, the 3D mapping's EPD picker); same readiness gate.
  const categoriesQuery = withApolloClient(() =>
    useGetCategoryTranslationsQuery(
      { languageCodes: ["EN"] },
      computed(() => ({ enabled: collectionsEnabled.value, fetchPolicy: "cache-first" as const }))
    )
  );
  const collectionsProvider = useCollectionsProvider();

  // STATE
  const owners = ref<Owner[] | null>(null);
  const activeMaterialFilters = ref<any>({});

  const libraryFilters = ref<LibraryFilters>({
    material: {
      collections: [],
      specifications: [],
      types: [],
      owners: [],
      datasources: [],
      categories: [],
      tags: [],
      organisations: [],
      indicators: [
        { Enum: 0, DisplayName: "GWP" },
        { Enum: 56, DisplayName: "GWP-IOBC" },
      ],
      minValidTo: "",
    },
    construction: {
      collections: [],
      units: [],
      tags: [],
      owners: [],
      organisations: [],
      // categories: [],
    },
  });

  const materialFilters = ref<any>({
    mappings: [
      { value: "include", label: "Include mapping" },
      { value: "exclude", label: "Exclude mapping" },
    ],
    states: [
      { value: "mapped", label: "Mapped" },
      { value: "unmapped", label: "Unmapped" },
      { value: "special", label: "Special Conditions" },
    ],
    datasources: [
      { value: 0, label: "Speckle" },
      { value: 1, label: "Custom" },
      { value: 2, label: "Combined" },
      { value: 3, label: "Epd" },
      { value: 4, label: "Excel upload" },
      { value: 5, label: "Forma" },
    ],
    sorting: [
      { value: false, label: "Name" },
      { value: false, label: "Amount" },
    ],
  });

  const inventoryFilters = computed(() => {
    const filters = materialFilters.value;

    const allMaterials = projectStore.allMaterials;

    const availableSources = new Set<number>();
    if (allMaterials && Array.isArray(allMaterials)) {
      allMaterials.forEach((material: SpeckleMaterial) => {
        // Check material's own source
        if (material.source !== undefined && material.source !== null) {
          availableSources.add(Number(material.source));
        }

        // Check children sources
        if (material.children && Array.isArray(material.children)) {
          material.children.forEach((child: SpeckleMaterial) => {
            if (child.source !== undefined && child.source !== null) {
              availableSources.add(Number(child.source));
            }
          });
        }
      });
    }

    const filteredDatasources = filters.datasources
      ? filters.datasources.filter((ds: any) => availableSources.has(ds.value))
      : [];

    // Classification code filter options derived from material additionalParameters
    const classificationParam = projectStore.project?.speckleConfiguration?.parameters?.classificationCode;
    const classificationCodeOptions: { value: string; label: string }[] = [];
    if (classificationParam && allMaterials.length > 0) {
      const codes = new Set<string>();
      let hasEmpty = false;
      allMaterials.forEach((material: SpeckleMaterial) => {
        const code = material.additionalParameters?.[classificationParam];
        if (code) {
          codes.add(code);
        } else {
          hasEmpty = true;
        }
      });
      [...codes].sort().forEach((code) => classificationCodeOptions.push({ value: code, label: code }));
      if (hasEmpty) {
        classificationCodeOptions.unshift({ value: "__empty__", label: "(No classification)" });
      }
    }

    // No `models` group: the inventory takes its model dimension from the toolbar's global Models
    // selector (dashboard store `selectedModels`), so the popover no longer duplicates it (RTLCA-5569).
    return {
      mappings: filters.mappings || [],
      states: filters.states || [],
      datasources: filteredDatasources.length > 0 ? filteredDatasources : filters.datasources || [],
      sorting: filters.sorting || [],
      classificationCodes: classificationCodeOptions,
    };
  });

  // ACTIONS
  const fetchOwners = async () => {
    // A non-200 throws out of axios rather than returning, and a 200 can still carry something that
    // is not a list. The old guard only caught null, so both ended up in .filter (JAVASCRIPT-VUE-2CE).
    let fetched: unknown;
    try {
      fetched = await getOwners();
    } catch (error) {
      Sentry.captureException(error);
      return;
    }

    if (!Array.isArray(fetched)) {
      Sentry.captureMessage("Owners response was not a list", { level: "warning", extra: { received: typeof fetched } });
      return;
    }

    owners.value = fetched as Owner[];

    libraryFilters.value["material"].owners = owners.value;

    const allowedNames = [
      "ROCKWOOL Nordics",
      "LCAbyg (2023)",
      "LCAbyg (2025)",
      "CLT Denmark",
      "Byggforsk (NO)",
      "Rakennustieto.fi (FI)",
      "Saint-Gobain",
      "Jysk Element",
    ];
    libraryFilters.value["construction"].owners = owners.value.filter((owner) => allowedNames.includes(owner.name));
  };

  watch(
    productTypes,
    (v) => {
      libraryFilters.value.material.types = A(v).map((t: ProductType) =>
        t.Enum === "CUSTOM" ? { ...t, value: t.value ?? false } : { ...t, code: PRODUCT_TYPE_CODE[t.Enum] }
      );
    },
    { deep: true }
  );

  watch(
    specificationForms,
    (v) => {
      libraryFilters.value.material.specifications = A(v).map((f: SpecificationForm) => ({
        ...f,
        code: SPEC_CODE[f.Enum],
      }));
    },
    { deep: true }
  );

  watch(
    tagsTypesForms,
    (v) => {
      const arr = A(v);

      libraryFilters.value.material.tags =
        arr.length && typeof arr[0] === "string" ? (arr as unknown as string[]) : (arr as Tag[]).map((t) => t.Enum ?? t);
      libraryFilters.value.construction.tags = libraryFilters.value.material.tags;
    },
    { deep: true }
  );

  watch(
    datasources,
    (v) => {
      libraryFilters.value.material.datasources = A(v);
    },
    { deep: true }
  );

  watch(
    categoriesQuery.result,
    (result) => {
      if (!result) return;
      const translations = result.translationCategory?.length ? result.translationCategory : result.fallbackTranslations;
      libraryFilters.value.material.categories = (translations ?? []).map((t) => ({ value: t.id, label: t.translation }));
    },
    { immediate: true }
  );

  watch(tenants, (v) => {
    if (v) {
      const orgs = Object.entries(v).map(([id, displayName]) => ({
        id,
        DisplayName: displayName,
      }));

      libraryFilters.value.material.organisations = orgs as any;
      libraryFilters.value.construction.organisations = orgs as any;
    }
  });

  // Initialize units from static unitOptions
  libraryFilters.value.construction.units = enumStore.unitOptions;

  watch(
    collectionsQuery.result,
    (data) => {
      if (data) {
        collectionsProvider.mapCollections(data);
      }
    },
    { immediate: true }
  );

  watch(
    () => collectionsProvider.collections,
    () => {
      libraryFilters.value.material.collections = collectionsProvider.getAll("material");
      libraryFilters.value.construction.collections = collectionsProvider.getAll("construction");
    },
    { immediate: true }
  );

  watch(
    itemsQuery.result,
    (data) => {
      data && collectionsProvider.mapItems(data);
    },
    { immediate: true }
  );

  // Apply only the "unclassified" inventory filter (no classification code).
  // `__empty__` is the sentinel used by the classificationCodes filter (see inventoryFilters / project.store).
  const showOnlyUnclassified = () => {
    activeMaterialFilters.value = { ...activeMaterialFilters.value, classificationCodes: { __empty__: true } };
  };

  // getters
  const activeFilters = computed(() => {
    return isConstruction.value ? libraryFilters.value.construction : libraryFilters.value.material;
  });

  return {
    // state
    libraryFilters,
    activeMaterialFilters,

    // actions
    fetchOwners,
    showOnlyUnclassified,

    activeFilters,
    inventoryFilters,
  };
});
